import React from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiLock, FiUser, FiDatabase, FiMail, FiCheck, FiGlobe, FiEye, FiTrash2, FiDownload, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import SEO from '../components/SEO';
import { trackEvent } from '../components/GoogleAnalytics';
import { useEffect } from 'react';

export default function Privacy() {
  useEffect(() => {
    trackEvent('page_view', 'navigation', 'privacy_policy');
  }, []);

  return (
    <>
      <SEO
        title="Privacy Policy - Bailebelle"
        description="Bailebelle's privacy policy. Learn how we collect, use, protect, and manage your personal information."
        keywords="privacy policy, data protection, GDPR, CCPA, privacy rights, data security"
        url="/privacy"
      />
      <div className="min-h-screen bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
            Privacy Policy
          </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiShield className="text-pink-500 mt-1" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Commitment to Your Privacy</h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  At Bailebelle, we are committed to protecting your privacy and ensuring the security of your personal information. 
                  This Privacy Policy explains how we collect, use, share, and protect your information when you visit our website, 
                  make a purchase, or interact with our services. We comply with applicable privacy laws, including GDPR, CCPA, 
                  and other data protection regulations.
                </p>
              </div>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Information We Collect</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiUser className="text-blue-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Personal Information</h3>
                    <p className="text-gray-400 mb-3 text-sm">
                      Information you provide directly to us:
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Name and contact information (email, phone, address)</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Payment and billing information</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Account credentials and profile information</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Communications and customer service inquiries</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <FiDatabase className="text-green-500 mt-1" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Usage Information</h3>
                    <p className="text-gray-400 mb-3 text-sm">
                      Information automatically collected when you use our website:
                    </p>
                    <ul className="text-gray-400 space-y-1 text-sm">
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Device information (IP address, browser type, operating system)</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Website usage data and analytics</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Cookies and similar tracking technologies</span>
                      </li>
                      <li className="flex items-start">
                        <FiCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" size={18} />
                        <span>Purchase history and preferences</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">How We Use Your Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Order Processing & Customer Service</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Process and fulfill your orders</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Send order confirmations and shipping updates</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Respond to customer service inquiries</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Process returns and refunds</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Marketing & Communications</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Send promotional emails and newsletters</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Notify you about new products and special offers</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Personalize your shopping experience</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Conduct market research and surveys</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Website Improvement</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Analyze website performance and usage</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Identify and fix technical issues</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Improve user experience and functionality</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Develop new features and services</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Legal & Security</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Prevent fraud and unauthorized access</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Comply with legal obligations and court orders</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Enforce our terms of service and policies</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-pink-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>Protect the rights and safety of our users</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Sharing */}
          <div className="bg-gray-900 rounded-2xl p-8 mb-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0">
                <FiGlobe className="text-purple-500 mt-1" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">How We Share Your Information</h2>
                <p className="text-gray-400 mb-6 text-sm">
                  We respect your privacy and only share your information in the following circumstances:
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Service Providers</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Payment Processors:</strong> Secure payment processing for transactions</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Shipping Partners:</strong> Delivery and logistics services</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Email Services:</strong> Transactional and marketing emails</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span><strong>Analytics:</strong> Google Analytics, Microsoft Clarity for website insights</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Legal Requirements</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li className="flex items-start">
                    <FiCheck className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>When required by law or legal process</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>To protect rights, property, or safety</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>In connection with business transfers or mergers</span>
                  </li>
                  <li className="flex items-start">
                    <FiCheck className="text-red-500 mt-1 mr-2 flex-shrink-0" size={18} />
                    <span>With your explicit consent when requested</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Privacy Rights */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Your Privacy Rights</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiEye className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Access</h3>
                <p className="text-gray-400 text-sm">
                  You have the right to request and receive a copy of the personal information we hold about you, 
                  including what data we have, how it's used, and why we have it.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiRefreshCw className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Rectification</h3>
                <p className="text-gray-400 text-sm">
                  You can request correction of inaccurate or incomplete personal information we have about you 
                  at any time.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiTrash2 className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Erasure</h3>
                <p className="text-gray-400 text-sm">
                  You have the right to request deletion of your personal information under certain circumstances, 
                  also known as the "right to be forgotten."
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiDownload className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Portability</h3>
                <p className="text-gray-400 text-sm">
                  You can request a copy of your data in a structured, commonly used, and machine-readable format 
                  to transfer to another service provider.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiAlertCircle className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Object</h3>
                <p className="text-gray-400 text-sm">
                  You can object to our processing of your personal information for direct marketing purposes 
                  or for our legitimate interests.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiLock className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Right to Restrict</h3>
                <p className="text-gray-400 text-sm">
                  You have the right to request restriction of processing of your personal information in certain 
                  situations.
                </p>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiLock className="text-blue-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Security Measures</h3>
                  <p className="text-gray-400 text-sm">
                    We implement industry-standard security measures to protect your personal information, including 
                    encryption, secure socket layer (SSL) technology, and regular security audits. Your payment 
                    information is processed through secure third-party payment processors who comply with PCI-DSS standards.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 rounded-2xl p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <FiShield className="text-green-500 mt-1" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Data Retention</h3>
                  <p className="text-gray-400 text-sm">
                    We retain your personal information only for as long as necessary to fulfill the purposes 
                    outlined in this policy, comply with legal obligations, resolve disputes, and enforce our 
                    agreements. When data is no longer needed, we securely delete or anonymize it.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Cookie Policy Link */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-center space-x-4">
              <FiShield className="text-pink-500" size={24} />
              <div className="flex-grow">
                <h3 className="text-lg font-semibold text-white mb-2">Cookie Usage</h3>
                <p className="text-gray-400 text-sm mb-4">
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze 
                  website traffic, and personalize content. For detailed information about our cookie usage, 
                  please review our Cookie Policy.
                </p>
                <Link
                  to="/cookies"
                  className="inline-flex items-center text-pink-500 hover:text-pink-600 transition-colors text-sm font-semibold"
                >
                  View Cookie Policy →
                </Link>
              </div>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiAlertCircle className="text-orange-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Children's Privacy</h3>
                <p className="text-gray-400 text-sm">
                  Our services are not intended for individuals under the age of 13 (or 16 in the EU). We do not 
                  knowingly collect personal information from children. If you believe we have inadvertently collected 
                  information from a child, please contact us immediately, and we will take steps to delete such 
                  information.
                </p>
              </div>
            </div>
          </div>

          {/* International Data Transfers */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiGlobe className="text-cyan-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">International Data Transfers</h3>
                <p className="text-gray-400 text-sm">
                  Your information may be transferred to and processed in countries other than your own. We ensure 
                  that appropriate safeguards are in place to protect your data in accordance with this Privacy Policy 
                  and applicable data protection laws, including GDPR and CCPA.
                </p>
              </div>
            </div>
          </div>

          {/* Policy Updates */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-12">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <FiMail className="text-yellow-500 mt-1" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Changes to This Policy</h3>
                <p className="text-gray-400 text-sm mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, services, 
                  or legal requirements. When we make changes, we will update the "Last updated" date at the bottom 
                  of this page. Your continued use of our services after changes become effective constitutes your 
                  acceptance of the revised policy.
                </p>
                <p className="text-yellow-400 text-sm font-semibold">
                  We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Questions About Our Privacy Policy?</h2>
              <p className="text-gray-400 mb-6">
                If you have any questions, concerns, or wish to exercise your privacy rights, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                >
                  <FiMail className="mr-2" size={18} />
                  Contact Us
                </Link>
                <Link
                  to="/cookies"
                  className="inline-flex items-center px-6 py-3 border-2 border-gray-600 text-white font-semibold rounded-lg hover:border-pink-500 transition-colors"
                >
                  Cookie Policy
                </Link>
              </div>
              <div className="text-gray-500 text-sm space-y-1">
                <p><strong>Data Controller:</strong> Bailebelle</p>
                <p>For GDPR-related inquiries, please contact our data protection officer at privacy@bailebelle.com</p>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
